import React, { useEffect, useMemo, useState } from 'react';
import { sumBy, uniqBy } from 'lodash';
import { capitalize, toLower } from 'lodash';

import Steps from 'Views/Components/Steps/Steps';
import { Display } from 'Views/Components/Button/Button';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import Wizard from 'Views/Components/Wizard/Wizard';
import StackedProgressBar from 'Views/Components/StackedProgressBar/StackedProgressBar';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import CompletedCourseAccordion from 'Views/Components/CompletedCourseAccordion/CompletedCourseAccordion';

import {
	IStudyPlanWizardData,
} from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import WizardSteps from 'Views/Pages/Frontend/StudyPlanWizard/WizardSteps';
import StudyPlanWizardHeader
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardHeader';
import StudyPlanWizardSidebar
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardSidebar';
import DuplicateCourseSelection
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';

import { useRequirementIndex }
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import ProgramRule
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/ProgramRule';
import ProgramComponents
, { handleSelectionError } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/ProgramComponents';
import { useCourseDuplicates }
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/useCourseDuplicates';
import { getRequiredUnitsMinMax } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { getSelectedCourseLocations } from 'Util/CourseUtils';
import RequirementsFulfilmentCheckPdf from 'Views/Pages/Pdf/RequirementsFulfilmentCheckPdf';
import useGetProgramRules from 'Queries/Hooks/ProgramRule/useGetProgramRules';
import { IGeneratedPlanFeedback } from 'Queries/Hooks/StudyPlan/types';
import { validateCourseSelection } from 'Queries/REST/StudyPlan/StudyPlanRequests';
import useUnitSelected from 'Hooks/useUnitSelected';
import { indexByPropertyArray } from 'Util/DataIndexUtils';
import { useMounted } from 'Hooks/useMounted';
import usePdfExport from 'Hooks/usePdfExport';

const RequirementFulfilmentCheckPage = () => {
	const mounted = useMounted();

	const {
		step,
		stepLabels,
		data,
		goToStep,
		previousStep,
	} = useWizardContext<IStudyPlanWizardData>();

	const {
		planSelections,
		requirementsById,
		coursesById,
		duplicateCourseIds,
		completedCourseIds,
		setCurrentComponentId,
		requirementTree,
		courseOfferingsById,
		requirementsByParentId,
		courseOfferingIdByCourseIdByRequirementId,
	} = useRequirementIndex();

	const planAssignedUserName = data?.studyPlan?.getAssignedUser().getDisplayName();

	const { data: programRules } = useGetProgramRules(data?.studyPlan?.programVersion.id);

	const [generatedPlanFeedback, setGeneratedPlanFeedback] = useState<IGeneratedPlanFeedback>();

	useEffect(() => {
		if (planSelections.length === 0) {
			return;
		}

		const incompletePlanSelections = planSelections
			.filter(selection => selection.completionStatus === 'INCOMPLETE');

		if (!data?.studyPlan) {
			return;
		}

		// Call study plan validation endpoint to get requisite errors
		validateCourseSelection(
			data?.studyPlan?.id,
			incompletePlanSelections
				.filter(selection => selection.courseOfferingId)
				.map(selection => selection.courseOfferingId!),
			completedCourseIds,
		)
			.then(response => {
				// handle expected feedback
				if (mounted.current) {
					setGeneratedPlanFeedback(response.data.feedback);
				}
			})
			.catch(handleSelectionError);
	}, [mounted, completedCourseIds, planSelections, data?.studyPlan]);

	const duplicateCourses = useCourseDuplicates(
		planSelections,
		duplicateCourseIds,
		requirementsById,
		coursesById,
	);

	const { totalUnitsSelected } = useUnitSelected({
		requirementTree: requirementTree,
		studyPlan: data?.studyPlan,
		coursesById,
		duplicateCourses,
	});

	// Index requisite warnings by course IDs
	const requisiteWarningsByCourseId = useMemo(
		() => generatedPlanFeedback
			? indexByPropertyArray(
				generatedPlanFeedback.requisiteWarnings,
				warning => warning.courseId,
			) : {},
		[generatedPlanFeedback],
	);

	/**
	 * Calculate course unit totals used for progress bar chart
	 *
	 * get incomplete and complete selectinos, and remove any duplicates
	 * for the same course
	 */
	const distinctIncompletePlanSelections = uniqBy(
		(data?.studyPlan?.getIncompletePlanSelections() || []),
		planSelection => planSelection.courseId,
	);

	const distinctCompletedPlanSelections = uniqBy(
		(data?.studyPlan?.getCompletedPlanSelections() || []),
		planSelection => planSelection.courseId,
	);

	const selectedUnitCount = sumBy(
		distinctIncompletePlanSelections,
		planSelection => coursesById[planSelection.courseId]?.units || 0,
	);

	const completedUnitCount = sumBy(
		distinctCompletedPlanSelections,
		planSelection => coursesById[planSelection.courseId]?.units || 0,
	);

	// map the distinct completed courses to their course entities
	// to provide to the completed course accordion
	const distinctCompletedCourses = distinctCompletedPlanSelections
		.map(selection => coursesById[selection.courseId]);

	const totalRequiredOfferings = useMemo(() => {
		/**
		 * get the components at the top of the tree.
		 * This selection is not subject to AND/OR display conditions,
		 * the unit values are always the components at the top
		 */
		const topLevelRequirements = Object.values(requirementTree);

		const totalRequiredUnits = topLevelRequirements.reduce(
			(acc, requirementNode) => {
				const { requirement } = requirementNode;

				const [min] = getRequiredUnitsMinMax(requirement.unitConditions);

				// if min is not found/defined, just add zero
				// as this requirement may not have a condition
				return (min || 0) + acc;
			},
			0,
		);

		return totalRequiredUnits;
	}, [requirementTree]);

	const { isGenerating, generatePdf } = usePdfExport({
		filename: `Requirements Fulfilment Check ${planAssignedUserName || ''}.pdf`,
		getPdfElement: () => {
			if (!data?.studyPlan || !programRules) {
				return null;
			}

			return (
				<RequirementsFulfilmentCheckPdf
					studyPlan={data.studyPlan}
					programRules={programRules}
					planSelections={planSelections}
					requirementsById={requirementsById}
					requirementTree={requirementTree}
					duplicateCourses={duplicateCourses}
					coursesById={coursesById}
					courseOfferingsById={courseOfferingsById}
					requirementsByParentId={requirementsByParentId}
					completedCourseIds={completedCourseIds}
					getCourseComponents={courseId => getSelectedCourseLocations(
						courseId,
						planSelections,
						requirementsById,
					).map(component => component.displayName)}
					requisiteWarningsByCourseId={requisiteWarningsByCourseId}
					totalUnitsSelected={totalUnitsSelected}
				/>
			);
		},
	});

	return (
		<>
			<StudyPlanWizardHeader />
			<Wizard.Body>
				<StudyPlanWizardSidebar
					showProgramDetails
					studyPlan={data?.studyPlan}
					onComponentClick={componentId => {
						setCurrentComponentId(componentId);
						goToStep(2);
					}}
				/>
				<Steps
					currentStep={step}
					steps={stepLabels}
					onStepClick={goToStep}
				/>
				<Wizard.Main>
					<div className="content step-3">
						<section className="page-header">
							<h3>
								{capitalize(window.lexicon.requirementsFulfilmentCheck)}
							</h3>
							<p>
								{data?.studyPlan?.programVersion.requirementFulfilmentDescription}
							</p>
						</section>
						<section className="program-structure">
							<h4>
								{capitalize(window.lexicon.program)} {toLower(window.lexicon.structure)}
							</h4>
							<h5>
								{data?.studyPlan?.programStructure.getDisplayName()}
							</h5>
							<p>
								{data?.studyPlan?.programStructure.description}
							</p>
							<StackedProgressBar
								completed={completedUnitCount}
								selected={selectedUnitCount}
								totalRequired={totalRequiredOfferings}
							/>
							<Conditional
								condition={!!completedUnitCount}
								component={() => (
									<CompletedCourseAccordion
										courses={distinctCompletedCourses}
										totalUnits={totalUnitsSelected}
										getCourseComponents={courseId => getSelectedCourseLocations(
											courseId,
											planSelections,
											requirementsById,
										).map(component => component.displayName)}
									/>
								)}
								componentProps={{ key: 'completed-course-accordion' }}
							/>
						</section>
						<section>
							{
								data?.studyPlan?.programVersion.id && programRules
									? (
										<ProgramRule
											programRules={programRules}
											planSelections={planSelections}
											coursesById={coursesById}
											completedCourseIds={completedCourseIds}
											description={data?.studyPlan?.programVersion.programRuleDescription}
											getCourseComponents={courseId => getSelectedCourseLocations(
												courseId,
												planSelections,
												requirementsById,
											).map(component => component.displayName)}
										/>
									) : null
							}
						</section>
						<section>
							<DuplicateCourseSelection
								duplicateCourses={duplicateCourses}
								completedCourseIds={completedCourseIds}
								description={data?.studyPlan?.programVersion.duplicateDescription}
							/>
						</section>
						<section>
							{
								data?.studyPlan?.id
									? (
										<ProgramComponents
											studyPlanId={data.studyPlan.id}
											planSelections={planSelections}
											coursesById={coursesById}
											courseOfferingsById={courseOfferingsById}
											requirementsByParentId={requirementsByParentId}
											requirementsById={requirementsById}
											completedCourseIds={completedCourseIds}
											duplicateCourses={duplicateCourses}
											allOfferingsByCourseIdByRequirementId={
												courseOfferingIdByCourseIdByRequirementId
											}
											requisiteWarningsByCourseId={requisiteWarningsByCourseId}
										/>
									) : null
							}
						</section>
					</div>
					<WizardSteps
						leftButtons={[
							{
								className: 'export-pdf-btn',
								title: 'Export PDF',
								icon: {
									icon: 'download',
									iconPos: 'icon-left',
								},
								disabled: isGenerating,
								onClick: () => generatePdf(),
							},
						]}
						rightButtons={[
							{
								title: 'Back',
								icon: {
									icon: 'arrow-back',
									iconPos: 'icon-left',
								},
								onClick: () => previousStep(),
							},
							{
								title: 'Next',
								display: Display.Solid,
								icon: {
									icon: 'arrow-forward',
									iconPos: 'icon-right',
								},
								onClick: () => goToStep(4),
							},
						]}
					/>
				</Wizard.Main>
			</Wizard.Body>
		</>
	);
};

export default RequirementFulfilmentCheckPage;
